/**
 * bimPrimitives — the unit geometries and the material recipe every device-model
 * builder shares.
 *
 * These lived in `useBimDeviceModels` until the card faces needed the same look
 * (`utils/deviceModelThumbnail.ts`): a water tank drawn on a card must read as
 * the very tank the 3D view draws, so both build it from the same primitives and
 * the same `materialOf` rule rather than two drifting copies.
 *
 * Shared geometries are module singletons: every part is placed by its mesh's
 * position/scale, so a dashboard with a hundred models still owns three
 * geometries per primitive.
 */

import * as THREE from 'three';

let unitBox: THREE.BoxGeometry | null = null;

/** One unit box, scaled per part. */
export function unitBoxGeometry(): THREE.BoxGeometry {
  if (!unitBox) unitBox = new THREE.BoxGeometry(1, 1, 1);
  return unitBox;
}

let unitCylinder: THREE.CylinderGeometry | null = null;

/**
 * The unit cylinder with its axis along +Z — three's own is +Y, so the geometry
 * is turned once here and every part scales it in its own frame.
 */
export function unitCylinderGeometry(): THREE.CylinderGeometry {
  if (!unitCylinder) {
    unitCylinder = new THREE.CylinderGeometry(0.5, 0.5, 1, 24);
    unitCylinder.rotateX(Math.PI / 2);
  }
  return unitCylinder;
}

/** The one material rule: double-sided, and transparent ones never write depth. */
export function materialOf(color: readonly number[], opacity = 1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color[0], color[1], color[2]),
    roughness: 0.85,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent: opacity < 1,
    opacity,
    depthWrite: opacity >= 1,
  });
}
